'use client'
import React, { useCallback, useEffect, useId, useRef, useState }  from 'react';
import clsx from 'clsx';
import { Icon } from '@/stories/atoms/Icon/Icon';
import { MoreCard, getPreviewImages } from './MoreCard';
import styles from './card-list.module.css';

/** nombre d'éléments à partir duquel les boutons de défilement apparaissent */
const MIN_ITEMS_FOR_NAV = 4

export type CardListProps = React.HTMLAttributes<HTMLDivElement> & {
  className?: string,
  /** carte « voir plus » ajoutée en dernière position quand la liste dépasse maxItems ; `label` sert de nom accessible */
  link?: {
    label: string,
    url: string,
    /** une image par carte, dans l'ordre des enfants : alimente l'aperçu en éventail */
    images?: (string | undefined)[]
  },
  /** affiche la carte lien même quand la liste ne dépasse pas maxItems */
  alwaysShowLink?: boolean
  /** nombre maximum de cartes affichées, carte « voir plus » comprise ; 'none' pour tout afficher */
  maxItems?: number | 'none'
}

export const CardList = ({className, children, link, alwaysShowLink, maxItems = 8, ...props}: CardListProps)=> {
  const id = useId()
  const containerRef = useRef<HTMLDivElement>(null)
  const [atStart, setAtStart] = useState(true)
  const [atEnd, setAtEnd] = useState(false)

  // au-delà de maxItems (ou toujours si alwaysShowLink), la dernière place revient au lien
  const limit = maxItems === 'none' ? Infinity : maxItems
  const items = React.Children.toArray(children)
  const showLink = !!link && (alwaysShowLink || items.length > limit)
  const visibleItems = items.slice(0, showLink ? limit - 1 : limit)
  const hasNav = visibleItems.length + (showLink ? 1 : 0) > MIN_ITEMS_FOR_NAV

  // désactive les boutons en butée (marge d'1px pour les arrondis de scrollLeft)
  const updateEdges = useCallback(() => {
    const container = containerRef.current
    if (!container) return
    setAtStart(container.scrollLeft <= 1)
    setAtEnd(container.scrollLeft + container.clientWidth >= container.scrollWidth - 1)
  }, [])

  useEffect(() => {
    const container = containerRef.current
    if (!hasNav || !container) return
    updateEdges()
    container.addEventListener('scroll', updateEdges, { passive: true })
    // ResizeObserver plutôt que window resize : couvre aussi une liste masquée au chargement (onglets) puis affichée
    const observer = new ResizeObserver(updateEdges)
    observer.observe(container)
    return () => {
      container.removeEventListener('scroll', updateEdges)
      observer.disconnect()
    }
  }, [hasNav, updateEdges])

  // défile d'une largeur visible, le scroll-snap recale sur la carte suivante
  const scroll = (direction: -1 | 1) => {
    const container = containerRef.current
    container?.scrollBy({ left: direction * container.clientWidth, behavior: 'smooth' })
  }

  return (
    <div className={clsx(styles.wrapper, className)} {...props}>
      {hasNav && (
        <div className={styles.nav}>
          <button
            type='button'
            className={styles.navButton}
            aria-controls={id}
            aria-label='Éléments précédents'
            disabled={atStart}
            onClick={() => scroll(-1)}>
            <Icon icon='angle-left' aria-hidden/>
          </button>
          <button
            type='button'
            className={styles.navButton}
            aria-controls={id}
            aria-label='Éléments suivants'
            disabled={atEnd}
            onClick={() => scroll(1)}>
            <Icon icon='angle-right' aria-hidden/>
          </button>
        </div>
      )}
      <div className={clsx(styles.container)} id={id} ref={containerRef}>
        {visibleItems.map(child => (
          <div className={styles.item} key={React.isValidElement(child) ? child.key : undefined}>{child}</div>
        ))}
        {showLink && (
          <div className={styles.item}>
            <MoreCard href={link.url} label={link.label || 'Tout afficher'} images={getPreviewImages(link.images, visibleItems.length)}/>
          </div>
        )}
      </div>
    </div>
  )
};
